/**
 * @author omriha
 * 05/02/2020
 */
import React, {useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import SMCButton from '../../../components/Simple/SMCButton';
import {SMCTab, SMCTabsContainer} from '../../../components/core/SMCTabs';
import {usePortalSettingsEditor} from './PortalSettingsEditorHelper';
import {currentPortalSelector, serviceDomainSettingsPermissionsSelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import PortalCertificates from './PortalCertificates';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import VerifyButtonsContainer from "../VerifyButtonsContainer";

/**
 * High level element to fetch, edit and save portal settings, all logic implemented inside.
 */
function PortalSettingsEditor({
    currentPortal,
    urlParams,
    serviceDomainSettingsPermissions
}) {
    const { t } = useTranslation();
    const { editorWidth, editorHeight } = useEditorDimensions(true);

    const CERTIFICATE_HELP_ID = '3792';

    const modifyPermission = serviceDomainSettingsPermissions ? serviceDomainSettingsPermissions.modify : true;
    const [currentlySelectedTab, setCurrentlySelectedTab] = useState('GENERAL');

    let defaultPage = 'general';
    const queryParams = queryString.parse(urlParams);
    if (queryParams && queryParams['page']) {
        defaultPage = queryParams['page'];
    }

    const {
        portalSettingsObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setPortalSettingsData,
        reloadSettingsFirmware,
        saveButtonOnClick,
        refreshButtonOnClick,
        revertButtonOnClick,
        reloadPageData
    } = usePortalSettingsEditor(defaultPage);
    
    // Data passed down to the editor pages
    const editorPageData = {
        portalSettingsObject,
        setEditorPageId: onPageChange,
        setPortalSettingsData,
        reloadSettingsFirmware,
        isPageDataLoaded,
        editorWidth,
        editorHeight
    }

    const _helpId = currentlySelectedTab === 'GENERAL' ? helpId : CERTIFICATE_HELP_ID;

    const editorTabContent = (
        <div className="ObjectEditor Tabs" style={{ width: editorWidth }}>
            {/* Header */}
            <div className="ObjectEditorHeader Tabs">
                <ButtonContainer>
                    <SMCButton icon="refresh" variant="ghost" disabled={!isPageDataLoaded} onClick={refreshButtonOnClick}>{t('SMC_GEN_REFRESH')}</SMCButton>
                </ButtonContainer>
                <LabelContainer label={t('SMP.NAME')+':'} value={currentPortal} />
            </div>
            <SMCDisabledProvider forceDisable={!modifyPermission}>
                <SMCEditor
                    editorPageData={editorPageData}
                    height={editorHeight}
                    width={editorWidth}
                    errorListByPage={errorMessagesByPageList}
                    chosenPageId={chosenPageId}
                    onPageChange={onPageChange}
                    isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                    isPageLoadFailed={isPageLoadFailed}
                    reloadData={reloadPageData}
                    pages={editorPages}
                />
            </SMCDisabledProvider>
        </div>
    );

    const certificatesTabContent = (
        <SMCDisabledProvider forceDisable={!modifyPermission}>
            <PortalCertificates
                width={editorWidth}
                height={editorHeight}
                serviceDomainName={currentPortal}
                portalSettingsObject={portalSettingsObject}
                setPortalSettingsData={setPortalSettingsData}
            />
        </SMCDisabledProvider>
    );

    return (
        <>
        <RouteLeavingGuard when={isObjectDirty} />
        <SMCPage loading={!isPageDataLoaded} helpId={_helpId}>
            <SMCTabsContainer width={editorWidth} tabOnClick={setCurrentlySelectedTab}>
                <SMCTab title={t('SMC_SETTINGS_TAB0')} id="GENERAL">
                    {editorTabContent}
                </SMCTab>
                <SMCTab title={t('SMC_SETTINGS_TAB2')} id="CERTIFICATES">
                    {certificatesTabContent}
                </SMCTab>
            </SMCTabsContainer>
        </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded || !modifyPermission}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = state => ({
    currentPortal: currentPortalSelector(state),
    serviceDomainSettingsPermissions: serviceDomainSettingsPermissionsSelector(state),
    urlParams: state.router.location.search
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(PortalSettingsEditor);